/**
 * DataEntryEntity — «הזנה חכמה» for a file that is a LIST of customers or suppliers, not money
 * movements (ADR-0037 phase 3, Turbo's DataEntryEntity / DataEntryList). The server reads every line
 * into the fields that will be written and checks it against the system (phone / ת.ז / email / code /
 * exact name, and the same line twice in the file); here each line is a row with what will be written.
 * The user fixes values, chooses what to do with a record that already exists (skip / fill its empty
 * fields / update it), approves with Enter, and can undo every line. Writes go through the customers /
 * suppliers services with the user's own permissions.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Banner, Button, EmptyState, EntityCard, Kbd, ProgressBar, Segmented, Select, Tag, TextInput, ckConfirm, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import {
  aiDataEntryApi,
  type ColumnGuessDto,
  type ColumnRole,
  type DataEntrySessionDto,
  type EntityAction,
  type EntityAnalysisDto,
  type EntityCommitResultDto,
  type EntityField,
  type EntityTarget,
  type EntityValues,
} from "../../../api/aiDataEntryApi";
import { notifyDataChanged } from "../../events";
import { DeSection } from "./DataEntryWorkspace";
import {
  ENTITY_ACTION_LABELS,
  ENTITY_STATE_META,
  entityApproveKind,
  entityBlocked,
  entityIsTodo,
  entityNeedsAttention,
  entityState,
  entitySummary,
  isTypingTarget,
  toLocalEntities,
  type LocalEntity,
} from "./dataEntryModel";

const FILTERS = [
  { value: "todo", label: "לטיפול" },
  { value: "attention", label: "דורשות בדיקה" },
  { value: "done", label: "טופלו" },
  { value: "all", label: "הכל" },
];
const SCAN_TEXT = ["קורא את הקובץ…", "ממפה עמודות לשדות…", "בודק כל שורה מול המערכת…", "מזהה רשומות שכבר קיימות…"];
const CHUNK = 50;
const errorText = (e: unknown, fallback: string) => (e as { message?: string })?.message || fallback;

function csvCell(v: unknown) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function DataEntryEntity({
  open,
  onClose,
  session,
  target,
  onBackToSetup,
  onOtherFile,
}: {
  open: boolean;
  onClose: () => void;
  session: DataEntrySessionDto;
  target: EntityTarget;
  onBackToSetup: () => void;
  onOtherFile: () => void;
}) {
  const toast = useCkToast();
  const [phase, setPhase] = useState<"scan" | "work" | "error">("scan");
  const [scanStep, setScanStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<EntityAnalysisDto | null>(null);
  const [entries, setEntries] = useState<LocalEntity[]>([]);
  const [sel, setSel] = useState(0);
  const [filter, setFilter] = useState("todo");
  const [tab, setTab] = useState("rows");
  const [mapDraft, setMapDraft] = useState<Record<string, ColumnRole> | null>(null);
  const [draft, setDraft] = useState<EntityValues | null>(null);
  const [busy, setBusy] = useState(false);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const committedAny = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const label = data?.label ?? (target === "customers" ? "לקוחות" : "ספקים");

  const run = useCallback(
    async (columns?: ColumnGuessDto[], { keepView = false }: { keepView?: boolean } = {}) => {
      if (!keepView) setPhase("scan");
      setError(null);
      setScanStep(0);
      const timer = window.setInterval(() => setScanStep((s) => Math.min(s + 1, SCAN_TEXT.length - 1)), 1400);
      try {
        const d = await aiDataEntryApi.entityAnalyze(session.id, { target, ...(columns ? { columns: columns.map((c) => ({ header: c.header, role: c.role })) } : {}) });
        setData(d);
        const local = toLocalEntities(d.entries, d.committed, entriesRef.current);
        setEntries(local);
        setMapDraft(null);
        if (!keepView) setSel(Math.max(0, local.findIndex((e) => entityIsTodo(entityState(e)))));
        setPhase("work");
      } catch (e) {
        const msg = errorText(e, "הסריקה נכשלה");
        if (keepView) toast.error(msg);
        else {
          setError(msg);
          setPhase("error");
        }
      } finally {
        window.clearInterval(timer);
      }
    },
    [session.id, target, toast],
  );

  // the first scan uses the columns as they are on the setup screen (the server saves them)
  useEffect(() => {
    if (open) void run(session.columns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const states = useMemo(() => entries.map(entityState), [entries]);
  const counts = useMemo(() => {
    const c = { total: entries.length, done: 0, passed: 0, ready: 0, attention: 0 };
    states.forEach((s) => {
      if (s === "done") c.done += 1;
      else if (s === "passed") c.passed += 1;
      else if (s === "ready") c.ready += 1;
      if (entityNeedsAttention(s)) c.attention += 1;
    });
    return c;
  }, [entries, states]);
  const todo = counts.total - counts.done - counts.passed;
  const visible = useMemo(
    () =>
      entries
        .map((_, i) => i)
        .filter((i) => {
          const s = states[i]!;
          if (filter === "todo") return entityIsTodo(s);
          if (filter === "attention") return entityNeedsAttention(s);
          if (filter === "done") return !entityIsTodo(s);
          return true;
        }),
    [entries, states, filter],
  );
  const cur = entries[sel] ?? null;
  const curState = cur ? states[sel]! : null;

  useEffect(() => setDraft(null), [sel]);

  const patch = (i: number, p: Partial<LocalEntity>) => setEntries((es) => es.map((e, j) => (j === i ? { ...e, ...p } : e)));
  const nextTodo = (from: number) => {
    const list = entriesRef.current;
    for (let k = from + 1; k < list.length; k += 1) if (entityIsTodo(entityState(list[k]!))) return k;
    for (let k = 0; k < from; k += 1) if (entityIsTodo(entityState(list[k]!))) return k;
    return from;
  };

  const applyResult = (i: number, r: EntityCommitResultDto | undefined) => {
    if (r?.ok && r.skipped) patch(i, { commit: { state: "passed", note: r.note } });
    else if (r?.ok && r.action && r.id) {
      committedAny.current = true;
      patch(i, { commit: { state: "done", action: r.action, id: r.id, label: r.label ?? "" } });
    } else patch(i, { commit: { state: "failed", error: r?.error ?? "הרישום נכשל" } });
  };
  const bodyOf = (e: LocalEntity) => ({ index: e.index, duplicateAction: e.duplicateAction, ...(e.edited ? { values: e.values } : {}) });

  /* ── approve (written at once, like Turbo's entity flow) ── */
  const approve = async (i: number) => {
    const e = entriesRef.current[i];
    if (!e) return;
    const s = entityState(e);
    if (s === "done" || s === "saving" || s === "passed") {
      setSel(nextTodo(i));
      return;
    }
    if (entityBlocked(s)) {
      toast.error("השורה לא תקינה — תקנו את הערכים בצד, או דלגו עליה");
      return;
    }
    if (entityApproveKind(e) === "skip") {
      patch(i, { commit: { state: "passed" } });
      setSel(nextTodo(i));
      return;
    }
    patch(i, { commit: { state: "saving" } });
    setSel(nextTodo(i));
    try {
      const res = await aiDataEntryApi.entityCommit(session.id, [bodyOf(e)]);
      applyResult(i, res.results[0]);
    } catch (err) {
      patch(i, { commit: { state: "failed", error: errorText(err, "הרישום נכשל") } });
    }
  };

  const approveAllReady = async () => {
    const list = entriesRef.current;
    const idx = list.map((_, i) => i).filter((i) => entityState(list[i]!) === "ready");
    if (!idx.length) return;
    const ok = await ckConfirm({
      title: `לרשום ${idx.length} שורות מוכנות?`,
      message: "שורות שדורשות בדיקה (קיימות, דומות או עם שגיאה) לא ייכללו. כל שורה ניתנת לביטול.",
      confirmLabel: `רישום ${idx.length}`,
    });
    if (!ok) return;
    setBusy(true);
    idx.forEach((i) => patch(i, { commit: { state: "saving" } }));
    try {
      for (let k = 0; k < idx.length; k += CHUNK) {
        const chunk = idx.slice(k, k + CHUNK);
        const res = await aiDataEntryApi.entityCommit(session.id, chunk.map((i) => bodyOf(list[i]!)));
        const byIndex = new Map(res.results.map((r) => [r.index, r]));
        chunk.forEach((i) => applyResult(i, byIndex.get(list[i]!.index)));
      }
      toast.success("השורות המוכנות נרשמו");
    } catch (err) {
      toast.error(errorText(err, "הרישום נכשל"));
      idx.forEach((i) => {
        if (entriesRef.current[i]?.commit.state === "saving") patch(i, { commit: { state: "idle" } });
      });
    } finally {
      setBusy(false);
    }
  };

  const undo = async (i: number) => {
    const e = entriesRef.current[i];
    if (!e) return;
    if (e.commit.state === "passed" || e.commit.state === "failed") {
      patch(i, { commit: { state: "idle" } });
      return;
    }
    if (e.commit.state !== "done") return;
    try {
      await aiDataEntryApi.entityUndo(session.id, e.index);
      committedAny.current = true;
      patch(i, { commit: { state: "idle" } });
      toast.success(e.commit.action === "created" ? "בוטל — הרשומה שנוצרה הועברה לארכיון" : "בוטל — הערכים הקודמים הוחזרו");
    } catch (err) {
      toast.error(errorText(err, "הביטול נכשל"));
    }
  };

  const pass = (i: number) => {
    patch(i, { commit: { state: "passed" } });
    setSel(nextTodo(i));
  };

  const saveDraft = async () => {
    if (!cur || !draft) return;
    setBusy(true);
    try {
      const values = { ...cur.values, ...draft };
      const next = await aiDataEntryApi.entityCheck(session.id, { index: cur.index, values, duplicateAction: cur.duplicateAction });
      patch(sel, { ...next, edited: true, commit: { state: "idle" } });
      setDraft(null);
      toast.success(next.status !== "error" ? "נבדק מחדש — השורה תקינה" : "נשמר; עדיין חסר משהו בשורה");
    } catch (err) {
      toast.error(errorText(err, "הבדיקה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  /* ── keyboard: Enter approves, Shift+Enter / ↑ back, ↓ next ── */
  useEffect(() => {
    if (!open || phase !== "work" || tab !== "rows") return undefined;
    const onKey = (ev: KeyboardEvent) => {
      const el = ev.target as HTMLElement | null;
      if (ev.key === "Enter" && !ev.shiftKey && !ev.altKey && !ev.ctrlKey) {
        if (isTypingTarget(el) || (el?.tagName === "BUTTON" && !el.closest(".de-dup") && !el.classList.contains("de-erow"))) return;
        ev.preventDefault();
        ev.stopPropagation();
        void approve(sel);
        return;
      }
      if (isTypingTarget(el)) return;
      const pos = visible.indexOf(sel);
      if (ev.key === "ArrowDown") {
        ev.preventDefault();
        setSel(visible[Math.min(visible.length - 1, pos + 1)] ?? sel);
      } else if (ev.key === "ArrowUp" || (ev.key === "Enter" && ev.shiftKey)) {
        ev.preventDefault();
        setSel(visible[Math.max(0, pos - 1)] ?? sel);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  });

  // the sheet opens focused on its close button (Enter would close it) — focus moves to the row
  useEffect(() => {
    if (phase !== "work" || tab !== "rows") return;
    const el = rootRef.current?.querySelector<HTMLElement>(`[data-ei="${sel}"]`);
    if (!el) return;
    el.scrollIntoView({ block: "nearest" });
    const active = document.activeElement;
    if (!isTypingTarget(active) && !(active as HTMLElement | null)?.closest?.(".de-side-col")) el.focus({ preventScroll: true });
  }, [sel, phase, tab]);

  const close = async () => {
    if (phase === "work" && todo > 0 && counts.done > 0) {
      const ok = await ckConfirm({ title: "לצאת?", message: `${todo} שורות עדיין לא טופלו. מה שכבר אושר נשמר במערכת.`, confirmLabel: "יציאה" });
      if (!ok) return;
    }
    if (committedAny.current) notifyDataChanged(target === "customers" ? "customer" : "supplier");
    onClose();
  };

  const downloadLeft = () => {
    const left = entries.filter((e) => entityIsTodo(entityState(e)));
    const headers = session.headers;
    const lines = [
      ["שורה", ...headers, "סיבה"].map(csvCell).join(","),
      ...left.map((e) =>
        [
          e.line,
          ...headers.map((h) => e.raw.find((c) => c.header === h)?.value ?? ""),
          e.commit.state === "failed" ? e.commit.error : e.errors[0] || e.warnings[0] || (e.status === "duplicate" ? "כבר קיים במערכת" : "לא אושרה"),
        ]
          .map(csvCell)
          .join(","),
      ),
    ];
    const blob = new Blob([`${String.fromCharCode(0xfeff)}${lines.join("\n")}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `הזנה-חכמה-${label}-שלא-נרשמו.csv`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  /* ── panes ── */
  const rowsPane = () => (
    <div className="de-main" ref={rootRef}>
      {!data?.can.create ? <Banner tone="warning">אין לך הרשאה להוסיף {label} — אפשר רק לעדכן רשומות קיימות.</Banner> : null}
      <div className="de-pane__h">
        <Segmented
          value={filter}
          onChange={setFilter}
          ariaLabel="סינון שורות"
          items={FILTERS.map((f) => ({ ...f, count: f.value === "todo" ? todo : f.value === "attention" ? counts.attention : f.value === "done" ? counts.done + counts.passed : counts.total }))}
        />
        <span className="de-muted">
          <Kbd>Enter</Kbd> אישור ומעבר · <Kbd>Shift</Kbd>+<Kbd>Enter</Kbd> אחורה
        </span>
      </div>
      {!visible.length ? (
        <EmptyState icon="check" title={filter === "todo" ? "כל השורות טופלו" : "אין שורות בסינון הזה"} />
      ) : (
        <div className="de-elist" role="listbox" aria-label="שורות הקובץ">
          {visible.map((i) => {
            const e = entries[i]!;
            const m = ENTITY_STATE_META[states[i]!];
            return (
              <button type="button" key={e.index} data-ei={i} role="option" aria-selected={i === sel} className={`de-erow ${i === sel ? "is-sel" : ""}`} onClick={() => setSel(i)}>
                <span className="de-erow__n">{e.line}</span>
                <span className="de-erow__t">{entitySummary(e)}</span>
                {e.existing && !e.existing.inFile ? <span className="de-muted de-erow__x">↔ {e.existing.label}</span> : null}
                <Tag tone={m.tone} icon={m.icon}>
                  {m.label}
                </Tag>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const mappingPane = () => {
    const cols = data?.columns ?? session.columns;
    const roleOptions = (Object.keys(session.roles) as ColumnRole[]).map((value) => ({ value, label: session.roles[value] }));
    const valueOf = (c: ColumnGuessDto) => (mapDraft && c.header in mapDraft ? mapDraft[c.header]! : c.role);
    return (
      <div className="de-main">
        <div className="de-pane__h">
          <b>עמודות הקובץ ← שדות במערכת</b>
          <span className="de-muted">שינוי כאן בודק את כל הקובץ מחדש · שם = חובה</span>
        </div>
        <div className="de-cols">
          {cols.map((c) => (
            <div key={c.header} className={`de-col ${valueOf(c) === "ignore" ? "is-off" : ""}`}>
              <div className="de-col__h">
                <b title={c.header}>{c.header}</b>
              </div>
              <Select
                value={valueOf(c)}
                options={roleOptions}
                onChange={(v) => setMapDraft((d) => ({ ...(d ?? {}), [c.header]: (v || "ignore") as ColumnRole }))}
                aria-label={`שדה לעמודה ${c.header}`}
              />
              {c.samples.length ? <small className="de-muted">{c.samples.slice(0, 2).join(" · ")}</small> : null}
            </div>
          ))}
        </div>
        <div className="de-actions-row">
          <Button
            variant="primary"
            icon="refresh"
            disabled={!mapDraft || busy}
            onClick={async () => {
              setBusy(true);
              await run(
                cols.map((c) => ({ ...c, role: valueOf(c) })),
                { keepView: true },
              );
              setBusy(false);
              toast.success("המיפוי עודכן והקובץ נבדק מחדש");
            }}
          >
            {busy ? "בודק…" : "החלה ובדיקה מחדש"}
          </Button>
          {mapDraft ? <Button onClick={() => setMapDraft(null)}>ביטול השינויים</Button> : null}
        </div>
      </div>
    );
  };

  const sidePane = () => {
    if (phase !== "work" || tab !== "rows" || !cur || !curState) {
      return (
        <DeSection title="הקובץ">
          <dl className="ck-kv">
            <dt>קובץ</dt>
            <dd>{session.fileName}</dd>
            <dt>סוג</dt>
            <dd>רשימת {label}</dd>
            {target === "suppliers" && session.ledger ? (
              <>
                <dt>קופה</dt>
                <dd>{session.ledger.name}</dd>
              </>
            ) : null}
            {data ? (
              <>
                <dt>שורות</dt>
                <dd>{counts.total}</dd>
                <dt>נרשמו</dt>
                <dd>{counts.done}</dd>
              </>
            ) : null}
          </dl>
        </DeSection>
      );
    }
    const m = ENTITY_STATE_META[curState];
    const locked = curState === "done" || curState === "saving";
    const changes = cur.duplicateAction === "merge_empty_fields" ? cur.merge : cur.duplicateAction === "update_existing" ? cur.update : [];
    const fieldDefs = data?.fields ?? [];
    const valueOf = (k: EntityField) => (draft && k in draft ? (draft[k] ?? "") : (cur.values[k] ?? ""));
    return (
      <>
        <DeSection title={`שורה ${cur.line}`} end={<Tag tone={m.tone} icon={m.icon}>{m.label}</Tag>}>
          {cur.commit.state === "failed" ? <Banner tone="danger">{cur.commit.error}</Banner> : null}
          {cur.commit.state === "passed" && cur.commit.note ? <Banner tone="info">{cur.commit.note}</Banner> : null}
          {cur.commit.state === "done" ? <Banner tone="success">{cur.commit.action === "created" ? `נוצר: ${cur.commit.label}` : `עודכן: ${cur.commit.label}`}</Banner> : null}
          {cur.errors.map((t) => (
            <Banner key={t} tone="danger">
              {t}
            </Banner>
          ))}
          {cur.warnings.map((t) => (
            <Banner key={t} tone="warning">
              {t}
            </Banner>
          ))}
          {cur.existing ? (
            <div className="de-dup">
              <b>
                {cur.existing.inFile ? `זהה ל${cur.existing.label}` : `${cur.existing.weak ? "אולי" : "כבר קיים במערכת"}: ${cur.existing.label}`}
                {cur.existing.sub ? <span className="de-muted"> · {cur.existing.sub}</span> : null}
              </b>
              <span className="de-muted">מה לעשות?</span>
              <Segmented
                value={cur.duplicateAction}
                onChange={(v) => !locked && patch(sel, { duplicateAction: v as EntityAction })}
                items={cur.actions.map((a) => ({ value: a, label: ENTITY_ACTION_LABELS[a] }))}
                ariaLabel="רשומה קיימת"
              />
              {changes.length ? (
                <ul className="de-changes">
                  {changes.map((c) => (
                    <li key={c.key}>
                      <b>{c.label}:</b> {c.from ? <s>{c.from}</s> : <span className="de-muted">ריק</span>} ← {c.to}
                    </li>
                  ))}
                </ul>
              ) : cur.duplicateAction === "merge_empty_fields" || cur.duplicateAction === "update_existing" ? (
                <span className="de-muted">אין בשורה שדות חדשים לרשומה הזו.</span>
              ) : null}
            </div>
          ) : null}
          {cur.fields.length ? (
            <dl className="ck-kv">
              {cur.fields.map((f) => (
                <Fragment key={f.key}>
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </Fragment>
              ))}
            </dl>
          ) : (
            <span className="de-muted">אין שדות ממופים בשורה הזו</span>
          )}
          <div className="de-actions-row">
            {curState === "done" || curState === "passed" ? (
              <Button icon="undo" onClick={() => void undo(sel)}>
                {curState === "done" ? "ביטול הרישום" : "החזרה לטיפול"}
              </Button>
            ) : (
              <>
                <Button variant="primary" icon="check" onClick={() => void approve(sel)} disabled={entityBlocked(curState) || curState === "saving"}>
                  {entityApproveKind(cur) === "skip"
                    ? "דילוג (לא לגעת)"
                    : cur.duplicateAction === "merge_empty_fields"
                      ? "השלמה"
                      : cur.duplicateAction === "update_existing"
                        ? "עדכון"
                        : "אישור ויצירה"}{" "}
                  <Kbd>Enter</Kbd>
                </Button>
                <Button icon="eyeOff" onClick={() => pass(sel)}>
                  דילוג
                </Button>
              </>
            )}
          </div>
        </DeSection>
        <DeSection title="הערכים שייכתבו" defaultOpen={entityBlocked(curState) || curState === "failed"}>
          <div className="de-cells">
            {fieldDefs.map((f) => (
              <label key={f.key} className="de-cell">
                <span className="de-muted">{f.label}</span>
                <TextInput
                  value={valueOf(f.key)}
                  disabled={locked}
                  aria-label={f.label}
                  onChange={(ev) => setDraft((d) => ({ ...(d ?? {}), [f.key]: ev.target.value || null }))}
                />
              </label>
            ))}
          </div>
          {draft ? (
            <Button variant="primary" icon="refresh" onClick={() => void saveDraft()} disabled={busy}>
              {busy ? "בודק…" : "שמירה ובדיקה מחדש"}
            </Button>
          ) : null}
        </DeSection>
      </>
    );
  };

  const scanPane = () => (
    <div className="de-main">
      {phase === "error" ? (
        <>
          <Banner tone="danger">{error}</Banner>
          <div className="de-actions-row">
            <Button variant="primary" onClick={() => void run()}>
              ניסיון נוסף
            </Button>
            <Button onClick={onBackToSetup}>חזרה לבחירת הסוג</Button>
          </div>
        </>
      ) : (
        <div className="de-scan">
          <ProgressBar />
          <b>{SCAN_TEXT[scanStep]}</b>
          <span className="de-muted">
            רשימת {label} · {session.totalRows} שורות
          </span>
        </div>
      )}
    </div>
  );

  const tabs =
    phase === "work"
      ? [
          { key: "rows", label: "שורה-שורה", count: todo, render: rowsPane },
          { key: "mapping", label: "עמודות", count: (data?.columns ?? []).length, render: mappingPane },
        ]
      : [{ key: "scan", label: "סריקה", render: scanPane }];
  const activeTab = tabs.some((t) => t.key === tab) ? tab : tabs[0]!.key;
  const pct = counts.total ? Math.round(((counts.done + counts.passed) / counts.total) * 100) : 0;

  const kpis =
    phase === "work"
      ? [
          { key: "total", label: "שורות", value: counts.total.toLocaleString("he-IL"), onClick: () => setFilter("all") },
          { key: "ready", label: "מוכנות", value: counts.ready.toLocaleString("he-IL"), onClick: () => setFilter("todo") },
          { key: "attention", label: "דורשות בדיקה", value: counts.attention.toLocaleString("he-IL"), attn: counts.attention > 0, onClick: () => setFilter("attention") },
          { key: "done", label: "נרשמו", value: counts.done.toLocaleString("he-IL"), sub: counts.total ? `${pct}% טופלו` : null, onClick: () => setFilter("done") },
        ]
      : [];
  const actions =
    phase === "work" && counts.ready
      ? [{ key: "all", label: `אישור כל המוכנות (${counts.ready})`, icon: "checkAll", primary: true, onClick: () => void approveAllReady(), disabled: busy }]
      : [];
  const more = [
    { key: "type", label: "חזרה לבחירת הסוג", icon: "right", onClick: onBackToSetup },
    ...(phase === "work" ? [{ key: "download", label: "הורדת השורות שלא נרשמו", icon: "download", onClick: downloadLeft }] : []),
    { key: "other", label: "קובץ אחר", icon: "upload", onClick: onOtherFile },
  ];

  return (
    <EntityCard
      open={open}
      onClose={() => void close()}
      width={1320}
      title={session.fileName}
      subtitle={<span>{`רשימת ${label}${data ? ` · ${counts.total} שורות` : ""}`}</span>}
      avatarText="✦"
      tags={[{ label: `רשימת ${label}`, tone: "primary" }, phase === "work" && counts.total ? { label: `${pct}% טופל`, tone: todo ? "neutral" : "success" } : null].filter(Boolean)}
      actions={actions}
      more={more}
      kpis={kpis}
      tabs={tabs}
      tab={activeTab}
      onTabChange={setTab}
      onPrev={phase === "work" && activeTab === "rows" ? () => setSel(visible[Math.max(0, visible.indexOf(sel) - 1)] ?? sel) : undefined}
      onNext={phase === "work" && activeTab === "rows" ? () => setSel(visible[Math.min(visible.length - 1, visible.indexOf(sel) + 1)] ?? sel) : undefined}
      detailsTop={<div className="de-side-col">{sidePane()}</div>}
    />
  );
}
